import React, { useState } from 'react';
import { Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Collapse, UncontrolledTooltip, Row, Col, Label } from 'reactstrap';
import __ from 'utils/i18n';
import { copyToClipboard } from 'utils';
import BottomAlert from 'components/BottomAlert';
import useSuccessAlert from 'hooks/useSuccessAlert';
import { calculatePlanStatus } from '../../_shared/planStatusUtils';
import '../../../styles.scss';
import { notification } from 'utils/notifications';

export const PlanDetailPanel = ({ plan, onEdit, onUpdateAmounts, onCancel, onViewSubscribers, onInviteByEmail }) => {
  const originalStatus = plan?.status;
  const isActive = originalStatus === 'Active';
  const isCancelled = !isActive;
  
  const [isWebsiteButtonOpen, setIsWebsiteButtonOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(isCancelled);
  
  // Hook para manejar alertas de éxito
  const { alertProps } = useSuccessAlert();

  if (!plan) return null;

  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatFrequency = (frequency) => {
    const frequencies = {
      Daily: __('dashboard.sections.recurringPayments.plans.table.frequencies.daily'),
      Weekly: __('dashboard.sections.recurringPayments.plans.table.frequencies.weekly'),
      Monthly: __('dashboard.sections.recurringPayments.plans.table.frequencies.monthly'),
      Quarterly: __('dashboard.sections.recurringPayments.plans.table.frequencies.quarterly'),
      Yearly: __('dashboard.sections.recurringPayments.plans.table.frequencies.yearly'),
    };
    return frequencies[frequency] || frequency;
  };

  // Calcular la próxima fecha de cobro para Quarterly
  const getNextQuarterlyBillingDate = (billingMonth, billingDay) => {
    const today = new Date();
    const targetMonth = parseInt(billingMonth) - 1; // 0-indexed
    const targetDay = Math.min(parseInt(billingDay), 28);
    
    // Calcular los meses del ciclo trimestral (cada 3 meses)
    const quarterMonths = [];
    for (let i = 0; i < 12; i += 3) {
      quarterMonths.push((targetMonth + i) % 12);
    }
    
    // Buscar la próxima fecha de cobro
    let nextDate = null;
    const currentYear = today.getFullYear();
    
    // Revisar los próximos 4 trimestres (hasta 12 meses adelante)
    for (let yearOffset = 0; yearOffset <= 1; yearOffset++) {
      for (let month of quarterMonths) {
        const candidateDate = new Date(currentYear + yearOffset, month, targetDay);
        if (candidateDate > today) {
          nextDate = candidateDate;
          break;
        }
      }
      if (nextDate) break;
    }
    
    if (!nextDate) return '-';
    
    const day = String(nextDate.getDate()).padStart(2, '0');
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const year = nextDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Calcular la próxima fecha de cobro para Yearly
  const getNextYearlyBillingDate = (billingMonth, billingDay) => {
    const today = new Date();
    const targetMonth = parseInt(billingMonth) - 1; // 0-indexed
    const targetDay = Math.min(parseInt(billingDay), 28);
    
    let nextDate = new Date(today.getFullYear(), targetMonth, targetDay);
    
    // Si la fecha ya pasó este año, usar el próximo año
    if (nextDate <= today) {
      nextDate = new Date(today.getFullYear() + 1, targetMonth, targetDay);
    }
    
    const day = String(nextDate.getDate()).padStart(2, '0');
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const year = nextDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusBadgeClass = () => {
    if (isCancelled) {
      return 'secondary';
    }
    
    const calculatedStatus = calculatePlanStatus(plan);
    const isChargeDay = calculatedStatus === 'ChargeDay';
    const isNextToCharge = calculatedStatus === 'NextToCharge';
    const isNoAmounts = calculatedStatus === 'NoAmounts';
    const isPendingCharge = calculatedStatus === 'PendingCharge';
    
    if (isChargeDay) {
      return 'primary';
    } else if (isNextToCharge || isPendingCharge) {
      return 'warning';
    } else if (isNoAmounts) {
      return 'danger';
    } else {
      return 'success';
    }
  };

  const getStatusText = () => {
    if (isCancelled) {
      return __('dashboard.sections.recurringPayments.plans.table.statuses.cancelled');
    }
    
    const calculatedStatus = calculatePlanStatus(plan);
    const isChargeDay = calculatedStatus === 'ChargeDay';
    const isNextToCharge = calculatedStatus === 'NextToCharge';
    const isNoAmounts = calculatedStatus === 'NoAmounts';
    const isPendingCharge = calculatedStatus === 'PendingCharge';
    
    if (isChargeDay) {
      return __('dashboard.sections.recurringPayments.plans.table.statuses.chargeDay');
    } else if (isNextToCharge) {
      return __('dashboard.sections.recurringPayments.plans.table.statuses.nextToCharge');
    } else if (isNoAmounts) {
      return __('dashboard.sections.recurringPayments.plans.table.statuses.noAmounts');
    } else if (isPendingCharge) {
      return __('dashboard.sections.recurringPayments.plans.table.statuses.pendingCharge');
    } else {
      return __('dashboard.sections.recurringPayments.plans.table.statuses.active');
    }
  };

  const isFixedPrice = plan.pricingType === 'Fixed';

  const getHtmlCode = () => {
    return `<a href="${plan.paymentLink || 'https://www.subscription.com/subscriptions/checkout?pr='}">\n  <button>${__('dashboard.sections.recurringPayments.plans.detail.websiteButtonSubscribe')}</button>\n</a>`;
  };

  const handleCopyCode = () => {
    copyToClipboard(getHtmlCode());
    setCodeCopied(true);
    notification.success(__('dashboard.sections.recurringPayments.plans.detail.codeCopiedMessage'));
    
    setTimeout(() => {
      setCodeCopied(false);
    }, 3000);
  };

  const handleCopyLink = () => {
    if (linkCopied) return;
    
    navigator.clipboard.writeText(plan.paymentLink).then(() => {
      setLinkCopied(true);
      notification.success(__('dashboard.sections.recurringPayments.plans.detail.linkCopiedMessage'));
      setTimeout(() => {
        setLinkCopied(false);
      }, 3000);
    });
  };

  const handleShareWhatsApp = () => {
    if (!plan.paymentLink) return;
    const text = encodeURIComponent(plan.paymentLink);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleInviteByEmail = () => {
    if (onInviteByEmail) {
      onInviteByEmail(plan);
    }
  };

  return (
    <div className="panel-flex">
      <div>
        <div className="recurring-payments-detail-container">
          <div className="text-center mb-4">
            <span
              style={(plan.planImage?.url || plan.productImageUrl) ? {} : {
                backgroundColor: plan.backgroundColor ? `#${plan.backgroundColor}` : ''
              }}
              className='catalog__product--image mx-auto mb-3'
            >
              {(plan.planImage?.url || plan.productImageUrl) ? (
                <img
                  className='img-cover'
                  src={plan.planImage?.url || plan.productImageUrl}
                  alt='Plan'
                  height='70px'
                  width='70px'
                />
              ) : (
                <span>{plan.name ? plan.name.charAt(0) : '?'}</span>
              )}
            </span>

            <div className="text-center mb-2">
              <h2 className="mb-0 font-weight-bold text-wrap">{plan.name}</h2>
            </div>

            <div className="d-flex justify-content-center align-items-center mb-2">
              <h3 className="mb-0 font-weight-bold text-wrap-amount">
                {isFixedPrice ? formatCurrency(plan.amount, plan.currency) : __('dashboard.sections.recurringPayments.plans.detail.variableAmount')}
                {isFixedPrice && <small className="text-muted ml-1">/{formatFrequency(plan.frequency)}</small>}
              </h3>
              
              {!isCancelled && (
                <UncontrolledDropdown className="ml-2">
                  <DropdownToggle tag="button" className="btn btn-link text-dark p-0">
                    <i className="mdi mdi-dots-vertical mdi-24px"></i>
                  </DropdownToggle>
                  <DropdownMenu right>
                    <DropdownItem onClick={() => onEdit(plan)}>
                      {__('dashboard.sections.recurringPayments.plans.actions.editPlan')}
                    </DropdownItem>
                    <DropdownItem className="text-danger" onClick={() => onCancel && onCancel(plan)}>
                      {__('dashboard.sections.recurringPayments.plans.actions.cancelPlan')}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </div>

            {plan.description && (
              <p className="text-muted mb-3 text-wrap">{plan.description}</p>
            )}

            <div>
              <span className={`badge badge-${getStatusBadgeClass()}`}>
                {getStatusText()}
              </span>
            </div>
          </div>

          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                {__('dashboard.sections.recurringPayments.plans.detail.subscribers')}
              </h5>
              {((plan.totalSubscriptions || 0) + 
                (plan.pausedSubscriptionsCount || 0) + 
                (plan.cancelledSubscriptionsCount || 0)) > 0 && (
                <Button 
                  color="link" 
                  className="p-0"
                  onClick={() => onViewSubscribers && onViewSubscribers(plan)}
                >
                  {__('dashboard.sections.recurringPayments.plans.detail.viewAll')}
                </Button>
              )}
            </div>
            <Row>
              <Col xs="4">
                <small className="text-muted d-block">
                  {__('dashboard.sections.recurringPayments.plans.detail.activeSubscribers')}
                </small>
                <h3 className="text-success mb-0">
                  {plan.totalSubscriptions || 0}
                </h3>
              </Col>
              <Col xs="4">
                <small className="text-muted d-block">
                  {__('dashboard.sections.recurringPayments.plans.detail.pausedSubscribers')}
                </small>
                <h3 className="mb-0">{plan.pausedSubscriptionsCount || 0}</h3>
              </Col>
              <Col xs="4">
                <small className="text-muted d-block">
                  {__('dashboard.sections.recurringPayments.plans.detail.canceledSubscribers')}
                </small>
                <h3 className="mb-0">{plan.cancelledSubscriptionsCount || 0}</h3>
              </Col>
            </Row>
          </div>

          <div>
            <div 
              onClick={() => {
                setShowMoreDetails(!showMoreDetails);
                if (!showMoreDetails) {
                  setIsWebsiteButtonOpen(false);
                }
              }}
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
            >
              <h6 className="mb-0">
                {__('dashboard.sections.recurringPayments.plans.detail.viewMoreDetails')}
              </h6>
              <i className={`mdi mdi-chevron-${showMoreDetails ? 'up' : 'down'} mdi-24px text-muted`}></i>
            </div>

            <Collapse isOpen={showMoreDetails}>
              <div className="mt-3">
                <Row>
                  <Col md="6" className="mb-3">
                    <small className="text-muted d-block mb-1">
                      {__('dashboard.sections.recurringPayments.plans.detail.createdAt')}
                    </small>
                    <span>{plan.createdAt ? new Date(plan.createdAt).toLocaleDateString('es-AR') : '-'}</span>
                  </Col>
                  <Col md="6" className="mb-3">
                    <small className="text-muted d-block mb-1">
                      {__('dashboard.sections.recurringPayments.plans.form.frequency')}
                    </small>
                    <span>{formatFrequency(plan.frequency)}</span>
                  </Col>
                  {plan.frequency === 'Weekly' && (
                    <Col md="6" className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {__('dashboard.sections.recurringPayments.plans.form.billingDay.label')}
                      </small>
                      <span>
                        {plan.billingDay ? (
                          <>
                            Todos los {[
                              __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.monday'),
                              __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.tuesday'),
                              __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.wednesday'),
                              __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.thursday'),
                              __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.friday'),
                              __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.saturday'),
                              __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.sunday')
                            ][plan.billingDay - 1]}
                          </>
                        ) : (
                          __('dashboard.sections.recurringPayments.plans.form.billingDay.subscription')
                        )}
                      </span>
                    </Col>
                  )}
                  {plan.frequency === 'Monthly' && (
                    <Col md="6" className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {__('dashboard.sections.recurringPayments.plans.form.billingDay.label')}
                      </small>
                      <span>
                        {plan.billingDay ? (
                          __('dashboard.sections.recurringPayments.plans.detail.billingDayFormat', { day: plan.billingDay })
                        ) : (
                          __('dashboard.sections.recurringPayments.plans.form.billingDay.subscription')
                        )}
                      </span>
                    </Col>
                  )}
                  {plan.frequency === 'Quarterly' && plan.billingDay && plan.billingMonth && (
                    <Col md="6" className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {__('dashboard.sections.recurringPayments.plans.form.billingDay.label')}
                      </small>
                      <span>
                        {getNextQuarterlyBillingDate(plan.billingMonth, plan.billingDay)}
                      </span>
                    </Col>
                  )}
                  {plan.frequency === 'Yearly' && plan.billingDay && plan.billingMonth && (
                    <Col md="6" className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {__('dashboard.sections.recurringPayments.plans.form.billingDay.label')}
                      </small>
                      <span>
                        {getNextYearlyBillingDate(plan.billingMonth, plan.billingDay)}
                      </span>
                    </Col>
                  )}
                  <Col md="6" className="mb-3">
                    <small className="text-muted d-block mb-1">
                      {__('dashboard.sections.recurringPayments.plans.detail.duration')}
                    </small>
                    <span>
                      {plan.duration ? 
                        __('dashboard.sections.recurringPayments.plans.table.durationValue', { count: plan.duration }) 
                        : __('dashboard.sections.recurringPayments.plans.detail.unlimited')
                      }
                    </span>
                  </Col>
                  <Col md="6" className="mb-3">
                    <small className="text-muted d-block mb-1">
                      {__('dashboard.sections.recurringPayments.plans.detail.trialPeriod')}
                    </small>
                    <span>
                      {plan.trialPeriodDays
                        ? __('dashboard.sections.recurringPayments.plans.form.trialPeriodDaysValue', { count: plan.trialPeriodDays })
                        : __('dashboard.sections.recurringPayments.plans.detail.noTrial')
                      }
                    </span>
                  </Col>
                  {plan.redirectUrl && (
                    <Col md="12" className="mb-3">
                      <small className="text-muted d-block mb-1">
                        {__('dashboard.sections.recurringPayments.plans.detail.redirectUrl')}
                      </small>
                      <div className="d-flex align-items-center">
                        <span className="text-truncate mr-2">{plan.redirectUrl}</span>
                        <Button 
                          color="link" 
                          size="sm"
                          onClick={() => window.open(plan.redirectUrl, '_blank')}
                          className="p-0"
                        >
                          <i className="mdi mdi-open-in-new mr-1"></i>
                          {__('dashboard.sections.recurringPayments.plans.detail.visit')}
                        </Button>
                      </div>
                    </Col>
                  )}
                  {isCancelled && plan.updatedAt && (
                    <Col md="12" className="mb-3">
                      <small className="text-danger d-block mb-1">
                        {__('dashboard.sections.recurringPayments.plans.detail.cancelledAt')}
                      </small>
                      <span className="text-danger font-weight-bold">
                        {new Date(plan.updatedAt).toLocaleDateString('es-AR')}
                      </span>
                    </Col>
                  )}
                </Row>
              </div>
            </Collapse>
          </div>
        </div>

          {!isCancelled && plan.paymentLink && (
            <>
              <div className="mb-3 text-center">
                <h5 className="mb-3">
                  {__('dashboard.sections.recurringPayments.plans.detail.shareLink')}
                </h5>
              </div>

              <div className="d-flex align-items-center justify-content-center mb-4">
                <div className="recurring-payments-rounded mr-2" style={{ border: '1px solid #dee2e6' }}>
                  <Button
                    id="copyLinkButtonDetail"
                    color="link"
                    onClick={handleCopyLink}
                    className="text-primary"
                    disabled={linkCopied}
                    style={{ textDecoration: 'none' }}
                  >
                    <i className={`mdi mdi-${linkCopied ? 'check' : 'link-variant'} mr-1`}></i>
                    {linkCopied ? __('dashboard.sections.recurringPayments.plans.detail.linkCopied') : __('dashboard.sections.recurringPayments.plans.table.copyLink')}
                  </Button>
                </div>
                <button
                  type="button"
                  className="recurring-payments-rounded mr-2 d-flex align-items-center justify-content-center"
                  style={{ border: '1px solid #dee2e6', width: '48px', height: '48px', background: '#fff' }}
                  onClick={handleShareWhatsApp}
                  aria-label="WhatsApp"
                  title="WhatsApp"
                >
                  <i className="mdi mdi-whatsapp mdi-24px text-success" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="recurring-payments-rounded d-flex align-items-center justify-content-center"
                  style={{ border: '1px solid #dee2e6', width: '48px', height: '48px', background: '#fff' }}
                  onClick={handleInviteByEmail}
                  aria-label={__('dashboard.sections.recurringPayments.plans.invite.title')}
                  title={__('dashboard.sections.recurringPayments.plans.invite.title')}
                >
                  <i className="mdi mdi-email-outline mdi-24px text-muted" aria-hidden="true" />
                </button>
              </div>

              <div className="recurring-payments-section mb-4">
              <div 
                onClick={() => {
                  setIsWebsiteButtonOpen(!isWebsiteButtonOpen);
                  if (!isWebsiteButtonOpen) {
                    setShowMoreDetails(false);
                  }
                }}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 mr-2">
                    {__('dashboard.sections.recurringPayments.plans.detail.websiteButton')}
                  </h5>
                  <i 
                    className="mdi mdi-help-circle text-muted" 
                    id="websiteButtonHelp"
                    style={{ cursor: 'pointer' }}
                  />
                  <UncontrolledTooltip placement="top" target="websiteButtonHelp">
                    {__('dashboard.sections.recurringPayments.plans.detail.websiteButton')}
                  </UncontrolledTooltip>
                </div>
                <i className={`mdi mdi-chevron-${isWebsiteButtonOpen ? 'up' : 'down'} mdi-24px text-muted`} />
              </div>

                <Collapse isOpen={isWebsiteButtonOpen}>
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Label className="mb-0">
                        {__('dashboard.sections.recurringPayments.plans.detail.websiteButtonCode')}
                      </Label>
                      <Button
                        color="link"
                        size="sm"
                        onClick={handleCopyCode}
                        className="p-0"
                      >
                        <i className={`mdi mdi-${codeCopied ? 'check' : 'content-copy'} mr-1`}></i>
                        {__('actions.copy')}
                      </Button>
                    </div>

                    <div className="bg-light recurring-payments-rounded p-3 mb-4">
                      <code className="d-block small text-dark text-truncate">
                        {getHtmlCode()}
                      </code>
                    </div>

                    <div className="text-center">
                      <small className="text-muted d-block mb-3">
                        {__('dashboard.sections.recurringPayments.plans.detail.websiteButtonDescription')}
                      </small>
                      <div className="d-inline-block bg-light recurring-payments-rounded p-3">
                        <Button 
                          color="primary"
                          onClick={(e) => e.preventDefault()}
                          style={{ cursor: 'default' }}
                        >
                          {__('dashboard.sections.recurringPayments.plans.detail.websiteButtonSubscribe')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </div>
            </>
          )}
      </div>

      {!isFixedPrice && !isCancelled && (plan?.totalSubscriptions || 0) > 0 && (
        <div className="panel-flex-footer">
          <Button 
            color="primary" 
            block
            className="font-weight-bold d-flex align-items-center justify-content-center"
            style={{ borderRadius: '8px' }}
            onClick={() => onUpdateAmounts && onUpdateAmounts(plan)}
          >
            <i className="mdi mdi-account-multiple mr-2"></i>
            {__('dashboard.sections.recurringPayments.plans.actions.updateAmounts')}
          </Button>
        </div>
      )}

      <BottomAlert {...alertProps} />
    </div>
  );
};

